import { inject, Injectable, OnDestroy, signal } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { USER_ACTIVITY_EVENTS } from 'src/app/app.constants';
import { InactivityService } from './inactivity.service';
import { WellService } from './well.service';
import { WsService } from './ws.service';

/**
 * Monitors user activity and disconnects the WebSocket after 5 minutes of inactivity, or once the
 * tab has been in the background for 1 minute. Web build only: app.config.ts binds
 * InactivityService to it outside HMI mode. HMI devices must stay connected at all times.
 *
 * Flow:
 * 1. Any DOM activity (click, keydown, touchstart, pointerdown) resets the 5-minute idle timer.
 * 2. When the timer fires, a warning toast appears with a 60-second countdown and a progress bar.
 * 3. If the user taps the toast → timer resets, they stay connected.
 * 4. If the countdown runs out (nobody tapped) → the session is released: it departs the current
 *    well, WsService.disconnect() closes the socket, and the reconnect overlay blocks the page.
 *
 * A background tab is released on its own clock: 1 minute after it was hidden, or at once if the
 * browser freezes it. Hidden tabs get no clicks, so the idle path would reach them too, but the
 * browser throttles a hidden tab's timers — to about once a minute after 5 minutes — and stops
 * them entirely in a frozen one, so an open socket would keep the server and the controller
 * streaming for as long as the tab stayed in the background.
 */
@Injectable()
export class InactivityKenLiveService extends InactivityService implements OnDestroy {
  private readonly _INACTIVITY_MS = 5 * 60 * 1000; // 5 minutes
  private readonly _WARNING_MS = 60 * 1000; // 60 seconds warning toast
  private readonly _HIDDEN_MS = 5 * 60 * 1000; // 5 minutes in the background

  /** Idle timeout in minutes, for display in the reconnect overlay. */
  override readonly inactivityMinutes = this._INACTIVITY_MS / (60 * 1000);

  /** True once the session has been disconnected due to inactivity.
   *  The layout component reads this to show a full-screen blocking overlay. */
  override readonly isDisconnected = signal(false);

  private _timer: ReturnType<typeof setTimeout> | null = null;
  private _activeToast: any | null = null;
  private _toastSubs: Subscription[] = [];
  private _countdownInterval: ReturnType<typeof setInterval> | null = null;
  // When the tab was last hidden (Date.now()), or null while it is visible.
  private _hiddenAt: number | null = null;
  private _hiddenTimer: ReturnType<typeof setTimeout> | null = null;

  // Bound references for event listeners
  private _boundReset = this._resetFromActivity.bind(this);
  private _boundVisibilityChange = this._onVisibilityChange.bind(this);
  private _boundFreeze = this._onFreeze.bind(this);

  private readonly _activityEvents = USER_ACTIVITY_EVENTS;

  private toastr = inject(ToastrService);
  private ws = inject(WsService);
  private wellService = inject(WellService);

  constructor() {
    super();
    this._activityEvents.forEach((ev) =>
      document.addEventListener(ev, this._boundReset, { passive: true, capture: true }),
    );
    document.addEventListener('visibilitychange', this._boundVisibilityChange);
    // Page Lifecycle API, Chromium only: fires just before the browser freezes a background
    // tab, which stops its timers outright. Elsewhere it never fires and the timers do the job.
    document.addEventListener('freeze', this._boundFreeze);
    this._reset();
    if (document.hidden) this._onVisibilityChange();
  }

  /**
   * Called by DOM activity events.
   * Ignores the event only after disconnect — any activity while the warning toast is visible
   * still resets the timer and dismisses it.
   */
  private _resetFromActivity(): void {
    if (this.isDisconnected()) return;
    this._reset();
  }

  /**
   * Dismisses any active warning toast and restarts the 5-minute idle timer.
   * Clears countdown intervals and subscriptions.
   */
  private _reset(): void {
    this._clearCountdown();
    this._clearActiveToast();
    if (this._timer !== null) clearTimeout(this._timer);
    this._timer = setTimeout(() => this._warn(), this._INACTIVITY_MS);
  }

  /**
   * Shows the inactivity warning toast with a live second-by-second countdown in the title.
   * Handles tap to reset or auto-disconnect after countdown.
   */
  private _warn(): void {
    // Counted down against a deadline rather than by ticks: a background tab's interval can fire
    // a minute apart, and a tick count would then stretch the 60 seconds to an hour.
    const deadline = Date.now() + this._WARNING_MS;
    let remaining = Math.ceil(this._WARNING_MS / 1000);

    this._activeToast = this.toastr.info(
      'Tap to stay connected.',
      `Data streaming will be interrupted after ${remaining}sec. due to ${this.inactivityMinutes} minutes of inactivity.`,
      {
        progressBar: true,
        timeOut: this._WARNING_MS,
        tapToDismiss: true,
        toastClass: 'ngx-toastr toast-info toast-white-info w-100 w-md-50 w-lg-25',
      },
    );

    this._countdownInterval = setInterval(() => {
      remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      // Direct DOM update for toast title countdown
      const titleEl = (
        this._activeToast?.portal?.location?.nativeElement as HTMLElement
      )?.querySelector('.toast-title');
      if (titleEl) {
        titleEl.textContent = `Data streaming will be interrupted after ${remaining} sec. due to ${this.inactivityMinutes} minutes of inactivity.`;
      }

      if (remaining <= 0) this._release();
    }, 1000);

    this._toastSubs.push(this._activeToast.onTap.pipe(take(1)).subscribe(() => this._reset()));
  }

  /**
   * Ends the session: departs the current well, closes the socket and blocks the page with the
   * reconnect overlay. Shared by the idle countdown and the background-tab release.
   */
  private _release(): void {
    if (this.isDisconnected()) return;
    this._clearCountdown();
    this._clearActiveToast();
    this._clearHiddenTimer();
    // Depart from the current well (sends SET_WELL_WIRES {on:false}) while
    // the socket is still open so the server removes this user from the
    // active list and stops sending events for this connection.
    this.wellService.setCurrentWellById();
    this.ws.disconnect();
    this._showReconnectOverlay();
  }

  /**
   * Starts the background clock when the tab is hidden and stops it when the tab is shown again.
   * Coming back checks the time actually spent hidden, in case the browser held the timer back.
   */
  private _onVisibilityChange(): void {
    if (this.isDisconnected()) return;
    if (document.hidden) {
      this._hiddenAt ??= Date.now();
      this._clearHiddenTimer();
      this._hiddenTimer = setTimeout(() => this._release(), this._HIDDEN_MS);
      return;
    }
    const hiddenFor = this._hiddenAt === null ? 0 : Date.now() - this._hiddenAt;
    this._hiddenAt = null;
    this._clearHiddenTimer();
    if (hiddenFor >= this._HIDDEN_MS) this._release();
  }

  /** The browser is about to freeze this background tab: no timer would run until it thaws, so
   *  release now, while the well departure can still be sent. */
  private _onFreeze(): void {
    if (document.hidden) this._release();
  }

  /** Cancels the pending background release, if any. */
  private _clearHiddenTimer(): void {
    if (this._hiddenTimer !== null) {
      clearTimeout(this._hiddenTimer);
      this._hiddenTimer = null;
    }
  }

  /**
   * Activates the full-screen reconnect overlay after the connection is closed due to inactivity.
   * The layout component reads isDisconnected() and renders a blocking overlay — the user must
   * tap the overlay button to reload and reconnect, preventing any other interaction.
   */
  private _showReconnectOverlay(): void {
    if (this._timer !== null) {
      clearTimeout(this._timer);
      this._timer = null;
    }
    this.isDisconnected.set(true);
  }

  /**
   * Clears the countdown interval if active.
   */
  private _clearCountdown(): void {
    if (this._countdownInterval !== null) {
      clearInterval(this._countdownInterval);
      this._countdownInterval = null;
    }
  }

  /**
   * Clears the active toast and unsubscribes from toast events.
   */
  private _clearActiveToast(): void {
    if (this._activeToast !== null) {
      this.toastr.clear(this._activeToast.toastId);
      this._activeToast = null;
      this._toastSubs.forEach((s) => s.unsubscribe());
      this._toastSubs = [];
    }
  }

  /**
   * Cleanup: removes event listeners, clears timers and subscriptions.
   */
  override ngOnDestroy(): void {
    this._activityEvents.forEach((ev) =>
      document.removeEventListener(ev, this._boundReset, { capture: true }),
    );
    document.removeEventListener('visibilitychange', this._boundVisibilityChange);
    document.removeEventListener('freeze', this._boundFreeze);
    if (this._timer !== null) clearTimeout(this._timer);
    this._clearHiddenTimer();
    this._clearCountdown();
    this._toastSubs.forEach((s) => s.unsubscribe());
    this._toastSubs = [];
  }
}
